<script setup>
// Fork JavaMifi: Settings → Organizations. Administrator membuat BU dan memilih
// inbox, agen (+ admin organisasi), dan canned response miliknya.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import OrganizationsAPI from 'dashboard/api/organizations';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);
const searchQuery = ref('');
const organizations = ref([]);
const inboxes = ref([]);
const agents = ref([]);
const cannedResponses = ref([]);

const showForm = ref(false);
const form = ref({});
const deleteTarget = ref(null);

const tableHeaders = computed(() => [
  t('ORGANIZATION_MGMT.TABLE_HEADER.NAME'),
  t('ORGANIZATION_MGMT.TABLE_HEADER.INBOXES'),
  t('ORGANIZATION_MGMT.TABLE_HEADER.AGENTS'),
  t('ORGANIZATION_MGMT.TABLE_HEADER.CANNED'),
  t('ORGANIZATION_MGMT.TABLE_HEADER.ACTION'),
]);

const filteredOrganizations = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return organizations.value;
  return picoSearch(organizations.value, query, ['name']);
});

const organizationName = id =>
  organizations.value.find(organization => organization.id === id)?.name;

const namesFor = (items, ids, key = 'name') =>
  items
    .filter(item => ids.includes(item.id))
    .map(item => item[key])
    .join(', ') || t('ORGANIZATION_MGMT.NONE');

const agentLabel = (organization, agent) =>
  organization.admin_ids.includes(agent.id) ? `${agent.name} (admin)` : agent.name;

const agentNames = organization =>
  agents.value
    .filter(agent => organization.agent_ids.includes(agent.id))
    .map(agent => agentLabel(organization, agent))
    .join(', ') || t('ORGANIZATION_MGMT.NONE');

// Keterangan "sekarang di BU lain" supaya admin tahu item itu akan dipindah.
const ownerHint = item => {
  if (!item.organization_id) return t('ORGANIZATION_MGMT.FORM.SHARED');
  if (item.organization_id === form.value.id) return '';
  return t('ORGANIZATION_MGMT.FORM.CURRENTLY_IN', {
    name: organizationName(item.organization_id),
  });
};

const fetchOrganizations = async () => {
  isLoading.value = true;
  try {
    const { data } = await OrganizationsAPI.get();
    organizations.value = data.organizations;
    inboxes.value = data.inboxes;
    agents.value = data.agents;
    cannedResponses.value = data.canned_responses;
  } finally {
    isLoading.value = false;
  }
};

const openForm = (organization = null) => {
  form.value = organization
    ? {
        id: organization.id,
        name: organization.name,
        inbox_ids: [...organization.inbox_ids],
        agent_ids: [...organization.agent_ids],
        admin_ids: [...organization.admin_ids],
        canned_response_ids: [...organization.canned_response_ids],
      }
    : {
        id: null,
        name: '',
        inbox_ids: [],
        agent_ids: [],
        admin_ids: [],
        canned_response_ids: [],
      };
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
};

const toggleAgent = agentId => {
  const { agent_ids: agentIds, admin_ids: adminIds } = form.value;
  if (agentIds.includes(agentId)) {
    form.value.agent_ids = agentIds.filter(id => id !== agentId);
    form.value.admin_ids = adminIds.filter(id => id !== agentId);
  } else {
    form.value.agent_ids = [...agentIds, agentId];
  }
};

const toggleAdmin = agentId => {
  const { admin_ids: adminIds } = form.value;
  form.value.admin_ids = adminIds.includes(agentId)
    ? adminIds.filter(id => id !== agentId)
    : [...adminIds, agentId];
};

const saveForm = async () => {
  if (!form.value.name.trim()) return;
  isSaving.value = true;
  const { id, ...payload } = form.value;
  try {
    if (id) {
      await OrganizationsAPI.update(id, payload);
    } else {
      await OrganizationsAPI.create(payload);
    }
    useAlert(t('ORGANIZATION_MGMT.API.SAVED'));
    closeForm();
    await fetchOrganizations();
  } catch {
    useAlert(t('ORGANIZATION_MGMT.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const confirmDelete = async () => {
  await OrganizationsAPI.delete(deleteTarget.value.id);
  useAlert(t('ORGANIZATION_MGMT.API.DELETED'));
  deleteTarget.value = null;
  await fetchOrganizations();
};

onMounted(fetchOrganizations);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('ORGANIZATION_MGMT.LOADING')"
    :no-records-found="!organizations.length"
    :no-records-message="$t('ORGANIZATION_MGMT.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('ORGANIZATION_MGMT.HEADER')"
        :description="$t('ORGANIZATION_MGMT.DESCRIPTION')"
        :search-placeholder="$t('ORGANIZATION_MGMT.SEARCH_PLACEHOLDER')"
      >
        <template v-if="organizations.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('ORGANIZATION_MGMT.COUNT', { n: organizations.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('ORGANIZATION_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openForm()"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredOrganizations"
        :no-data-message="$t('ORGANIZATION_MGMT.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="organization in items"
            :key="organization.id"
            :item="organization"
          >
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ organization.name }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ namesFor(inboxes, organization.inbox_ids) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ agentNames(organization) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{
                    namesFor(
                      cannedResponses,
                      organization.canned_response_ids,
                      'short_code'
                    )
                  }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openForm(organization)"
                  />
                  <Button
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="deleteTarget = organization"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showForm" :on-close="closeForm" size="medium">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          :header-title="
            form.id
              ? $t('ORGANIZATION_MGMT.FORM.EDIT_TITLE')
              : $t('ORGANIZATION_MGMT.FORM.ADD_TITLE')
          "
          :header-content="$t('ORGANIZATION_MGMT.FORM.DESC')"
        />
        <form
          class="flex flex-col gap-5 px-8 pt-4 pb-8"
          @submit.prevent="saveForm"
        >
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ $t('ORGANIZATION_MGMT.FORM.NAME') }}
            <input
              v-model="form.name"
              type="text"
              data-testid="organization-name"
              :placeholder="$t('ORGANIZATION_MGMT.FORM.NAME_PLACEHOLDER')"
            />
          </label>

          <fieldset class="flex flex-col gap-2">
            <legend class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('ORGANIZATION_MGMT.FORM.INBOXES') }}
            </legend>
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                v-model="form.inbox_ids"
                type="checkbox"
                class="!m-0"
                :value="inbox.id"
              />
              {{ inbox.name }}
              <span class="text-xs text-n-slate-10">{{ ownerHint(inbox) }}</span>
            </label>
          </fieldset>

          <fieldset class="flex flex-col gap-2">
            <legend class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('ORGANIZATION_MGMT.FORM.AGENTS') }}
            </legend>
            <div
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center justify-between gap-2 text-sm text-n-slate-12"
            >
              <label class="flex items-center gap-2">
                <input
                  type="checkbox"
                  class="!m-0"
                  :checked="form.agent_ids.includes(agent.id)"
                  @change="toggleAgent(agent.id)"
                />
                {{ agent.name }}
                <span class="text-xs text-n-slate-10">
                  {{ agent.email }} {{ ownerHint(agent) }}
                </span>
              </label>
              <label
                v-if="form.agent_ids.includes(agent.id)"
                class="flex items-center gap-1 text-xs text-n-slate-11"
              >
                <input
                  type="checkbox"
                  class="!m-0"
                  :checked="form.admin_ids.includes(agent.id)"
                  @change="toggleAdmin(agent.id)"
                />
                {{ $t('ORGANIZATION_MGMT.FORM.ADMIN') }}
              </label>
            </div>
          </fieldset>

          <fieldset class="flex flex-col gap-2">
            <legend class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('ORGANIZATION_MGMT.FORM.CANNED') }}
            </legend>
            <label
              v-for="canned in cannedResponses"
              :key="canned.id"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                v-model="form.canned_response_ids"
                type="checkbox"
                class="!m-0"
                :value="canned.id"
              />
              {{ canned.short_code }}
              <span class="text-xs text-n-slate-10">{{ ownerHint(canned) }}</span>
            </label>
          </fieldset>

          <div class="flex justify-end gap-2">
            <Button
              faded
              slate
              type="reset"
              :label="$t('ORGANIZATION_MGMT.FORM.CANCEL')"
              @click.prevent="closeForm"
            />
            <Button
              type="submit"
              :label="$t('ORGANIZATION_MGMT.FORM.SAVE')"
              :is-loading="isSaving"
              :disabled="!form.name?.trim()"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <woot-delete-modal
      :show="!!deleteTarget"
      :on-close="() => (deleteTarget = null)"
      :on-confirm="confirmDelete"
      :title="$t('ORGANIZATION_MGMT.DELETE.TITLE')"
      :message="$t('ORGANIZATION_MGMT.DELETE.MESSAGE')"
      :message-value="deleteTarget?.name"
      :confirm-text="$t('ORGANIZATION_MGMT.DELETE.YES')"
      :reject-text="$t('ORGANIZATION_MGMT.DELETE.NO')"
    />
  </SettingsLayout>
</template>
